'use client';

import { motion, useSpring, useTransform } from 'motion/react';
import { useEffect, useState } from 'react';

import { cn } from '@/utils/cn';

import styles from './FrontPageHero.module.scss';

type Props = {
  setUserPaused?: (paused: boolean) => void;
  videoElement: HTMLVideoElement | null;
};

export const FrontPagePlayPauseButton: React.FC<Props> = ({
  setUserPaused,
  videoElement,
}) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(10); // Default to prevent NaN issues

  const smoothProgress = useSpring(0, {
    stiffness: 50,
    damping: 20,
    mass: 1,
  });

  const strokeOffset = useTransform(smoothProgress, [0, 100], [282, 0]);

  useEffect(() => {
    if (!videoElement) return;

    setDuration(videoElement.duration);

    const updateProgress = (seconds: number) => {
      setProgress((seconds / duration) * 100);
    };

    videoElement.addEventListener('timeupdate', () => {
      updateProgress(videoElement.currentTime);
    });

    videoElement.addEventListener('play', () => setIsPlaying(true));
    videoElement.addEventListener('pause', () => setIsPlaying(false));

    return () => {
      videoElement.removeEventListener('timeupdate', () => {});
      videoElement.removeEventListener('play', () => {});
      videoElement.removeEventListener('pause', () => {});
    };
  }, [videoElement, duration]);

  useEffect(() => {
    smoothProgress.set(progress);
  }, [progress, smoothProgress]);

  const togglePlay = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!videoElement) return;
    const paused = videoElement.paused;
    if (paused) {
      videoElement.play();
      setIsPlaying(true);
      setUserPaused?.(false);
    } else {
      videoElement.pause();
      setIsPlaying(false);
      setUserPaused?.(true);
    }
  };

  return (
    <button
      type='button'
      className={cn(styles.playPauseButton, isPlaying && styles.playing)}
      onClick={togglePlay}
      aria-label='Play/Pause'
    >
      {videoElement && (
        <svg className={styles.icon} viewBox='0 0 100 100'>
          <motion.circle
            cx='50'
            cy='50'
            r='45'
            stroke='rgba(255, 255, 255, 0.9)'
            strokeWidth='5'
            fill='none'
            strokeDasharray='282'
            strokeDashoffset={strokeOffset}
            style={{ willChange: 'stroke-dashoffset' }}
          />
        </svg>
      )}
      <div className={styles.playPause}>
        {isPlaying ? (
          <svg
            width='8'
            height='8'
            viewBox='0 0 8 8'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <g clipPath='url(#clip0_1946_5558)'>
              <path
                d='M6.75 1.49988V6.49988C6.75 6.63249 6.69732 6.75966 6.60355 6.85343C6.50979 6.9472 6.38261 6.99988 6.25 6.99988H5C4.86739 6.99988 4.74021 6.9472 4.64645 6.85343C4.55268 6.75966 4.5 6.63249 4.5 6.49988V1.49988C4.5 1.36727 4.55268 1.24009 4.64645 1.14632C4.74021 1.05256 4.86739 0.999878 5 0.999878H6.25C6.38261 0.999878 6.50979 1.05256 6.60355 1.14632C6.69732 1.24009 6.75 1.36727 6.75 1.49988ZM3 0.999878H1.75C1.61739 0.999878 1.49021 1.05256 1.39645 1.14632C1.30268 1.24009 1.25 1.36727 1.25 1.49988V6.49988C1.25 6.63249 1.30268 6.75966 1.39645 6.85343C1.49021 6.9472 1.61739 6.99988 1.75 6.99988H3C3.13261 6.99988 3.25979 6.9472 3.35355 6.85343C3.44732 6.75966 3.5 6.63249 3.5 6.49988V1.49988C3.5 1.36727 3.44732 1.24009 3.35355 1.14632C3.25979 1.05256 3.13261 0.999878 3 0.999878Z'
                fill='#2a0d5d'
              />
            </g>
            <defs>
              <clipPath id='clip0_1946_5558'>
                <rect width='8' height='8' fill='white' />
              </clipPath>
            </defs>
          </svg>
        ) : (
          <svg
            width='32'
            height='33'
            viewBox='0 0 32 33'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <g clipPath='url(#clip0_1946_4858)'>
              <path
                d='M30 16.9505C30.0008 17.29 29.9138 17.624 29.7473 17.9199C29.5808 18.2158 29.3406 18.4636 29.05 18.6392L11.04 29.6567C10.7364 29.8426 10.3886 29.9441 10.0326 29.9507C9.67661 29.9573 9.32532 29.8688 9.015 29.6942C8.70764 29.5224 8.4516 29.2717 8.2732 28.9681C8.09481 28.6645 8.00051 28.3188 8 27.9667V5.9342C8.00051 5.58206 8.09481 5.23641 8.2732 4.93279C8.4516 4.62918 8.70764 4.37856 9.015 4.2067C9.32532 4.03214 9.67661 3.94358 10.0326 3.95017C10.3886 3.95676 10.7364 4.05827 11.04 4.2442L29.05 15.2617C29.3406 15.4373 29.5808 15.6851 29.7473 15.981C29.9138 16.2769 30.0008 16.6109 30 16.9505Z'
                fill='#2a0d5d'
              />
            </g>
            <defs>
              <clipPath id='clip0_1946_4858'>
                <rect
                  width='32'
                  height='32'
                  fill='#2a0d5d'
                  transform='translate(0 0.950439)'
                />
              </clipPath>
            </defs>
          </svg>
        )}
      </div>
    </button>
  );
};
